import styles from './index.module.scss';
import { defineComponent, h, PropType } from 'vue';
import { I } from '@vaaas/fpts/combinator';
import type { Parser } from '@phi/util/types';
import { do_nothing } from '@phi/util';
import { utility } from '@phi/components';
import { isEnterOrSpace } from '@frontend/keyboard';
import { Autocomplete } from '../types';

type Variant =
    'default'
    | 'outlined'

export const FormPassword = defineComponent({
    name: 'FormPassword',

    props: {
        value: {
            required: true,
            type: null as any as PropType<string | undefined>,
        },

        placeholder: {
            required: false,
            type: String,
        },

        change: {
            required: false,
            type: Boolean,
            default: false,
        },

        disabled: {
            required: false,
            type: Boolean,
            default: false,
        },

        parser: {
            required: false,
            type: Function as PropType<Parser<string>>,
            default: I,
        },

        variant: {
            required: false,
            type: String as PropType<Variant>,
            default: 'default',
        },

        id: { required: false, type: String },

        name: { required: false, type: String },

        autocomplete: { required: false, type: String as PropType<Autocomplete> }
    },

    emits: {
        'update:value': (x: string) => true,
    },

    data() {
        return {
            type: 'password' as 'password' | 'text',
        };
    },

    methods: {
        update(x: InputEvent) {
            const value = this.parser((x.target as HTMLInputElement).value);
            if (!(value instanceof Error))
                this.$emit('update:value', value);
        },

        toggle() {
            this.type =
                this.type === 'password'
                    ? 'text'
                    : 'password';
        },

        on_key_down(e: KeyboardEvent) {
            if (isEnterOrSpace(e))
                this.toggle();
        }
    },

    render() {
        return h('span', {
            class: {
                'form-password': true,
                [utility.flex]: true,
                [utility.align_center]: true,
                [utility.w_100]: true,
                [utility.round]: true,
                [styles.wrapper]: true,
                [styles.outlined]: this.variant === 'outlined',
                [styles.disabled]: this.disabled === true,
            },
        }, [
            h('input', {
                id: this.id,
                name: this.name,
                class: [
                    utility.flexible,
                    styles.input,
                ],
                type: this.type,
                value: this.value,
                onInput: this.change ? do_nothing : this.update,
                onChange: this.change ? this.update : do_nothing,
                placeholder: this.placeholder,
                disabled: this.disabled,
                autocomplete: this.autocomplete,
            }),

            h('span', {
                tabIndex: '0',
                class: {
                    [utility.pointer]: true,
                    [utility.slightly_faded]: this.type === 'password',
                    [utility.color_brand]: this.type === 'text',
                },
                onClick: this.toggle,
                onKeydown: this.on_key_down,
            },  this.type === 'password' ? 'Show' : 'Hide'),
        ]);
    }
});
